import { useAutoAnimate } from '@formkit/auto-animate/react'
import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getBreakpointUp,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { Fragment } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Box from 'src/components/Box/Box'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import { MoreDropdownOption } from 'src/components/MoreDropdown/MoreDropdown'
import SvgChevronDown from 'src/components/Svgs/SvgChevronDown'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import { useDropdownOpen } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import Lnk from 'src/navigation/Lnk'
import { organizationSettingsUrl, peopleMembersUrl } from 'src/navigation/urls'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import styled, { css } from 'styled-components'

interface OrgAdminBoxProps {
  className?: string
  orgName: string | undefined
  subtitle: React.ReactNode
  orgBookkeeper?: boolean
  topBox?: boolean
}

const StyledDiv = styled.div<{
  grayed: boolean
  underlined: boolean
}>`
  color: ${themeConstants.colors.cta};
  display: flex;
  align-items: center;
  padding: 10px 20px 5px 20px;
  ${getFontCss(
    14,
    themeConstants.colors.cta,
    styleConstants.fontFamilies.medium,
  )};
  font-size: 12px;
  white-space: nowrap;

  ${getBreakpointUp(breakpointSizes.SM)(css`
    &:first-child {
      display: none;
    }
  `)}
`

const OrgAdminBox: React.FC<OrgAdminBoxProps> = (props): React.ReactElement => {
  const translate = useTranslate()

  const [dropdownOptionsRef] = useAutoAnimate({
    duration: 125,
    easing: 'ease-in',
  })
  const { dropdownOpen, divRef, handleOpenerClick, setDropdownOpen } =
    useDropdownOpen({
      closeOnSecondClick: true,
      closeOnAnyClick: true,
    })
  const user = useSelector(selectUserListModelFromState)
  const organizationId = user?.organizationId
  const { analytics } = useAnalyticsContext()

  const handleOrgInfoAnalytics = (key: string | number | undefined): void => {
    if (key === 'Organization Settings') {
      analytics?.track(
        'home manage organization - open org info - text link press',
        {
          screen: '/home',
          interaction: 'text link press',
          intent: 'open org info',
          user: user?.id,
        },
      )
    }
  }

  const organizationInfoOption: MoreDropdownOption[] | undefined = [
    {
      key: 'Organization Settings',
      label: translate('Organization Settings'),
      to: organizationSettingsUrl(
        organizationId !== undefined ? organizationId : '',
      ),
      ariaLabelSource: 'Organization Settings',
    },
  ]
  const teamMembersOption: MoreDropdownOption[] | undefined = [
    {
      label: translate('Organization Members'),
      to: peopleMembersUrl(),
      ariaLabelSource: 'Organization Members',
    },
  ]
  const organizationDropdownOptions: MoreDropdownOption[] = [
    ...(organizationInfoOption || []),
    ...(teamMembersOption || []),
  ]
  return (
    <Box
      css={css`
        display: flex;
        flex-direction: column;
        background-color: ${styleConstants.colors.gray_lightest};
      `}
      orgBookkeeper
      topBox={props.topBox}
      className={props.className}
    >
      <span
        css={css`
          display: flex;
          ${getFontCss(
            14,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {props.subtitle}
      </span>
      <div
        css={css`
          display: flex;
          position: relative;
          gap: 5px;
        `}
      >
        <span
          css={css`
            display: inline-block;
            flex-grow: 1;
            align-items: center;
            margin-top: 10px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            ${getBreakpointDown(breakpointSizes.XSM)(css`
              ${getFontCss(
                24,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              margin-bottom: 2px;
            `)}
            ${getFontCss(
              36,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.medium,
            )};
          `}
        >
          {props.orgName}
        </span>
        <div
          ref={divRef}
          onMouseEnter={(): void => {
            setDropdownOpen(true)
          }}
          onMouseLeave={(): void => {
            setDropdownOpen(false)
          }}
          onBlur={(): undefined => {
            return
          }}
          onFocus={(): undefined => {
            return
          }}
        >
          <span
            id="manageOrg"
            onClick={handleOpenerClick}
            onKeyPress={handleOpenerClick}
            role="button"
            tabIndex={0}
            css={css`
              ${getBreakpointUp(breakpointSizes.LG)(css`
                margin-left: 36px;
              `)}
              ${getBreakpointOnly(breakpointSizes.MD)(css`
                margin-left: 24px;
              `)}
              ${getFontCss(
                14,
                themeConstants.colors.cta,
                styleConstants.fontFamilies.medium,
              )};
              float: right;
              margin-top: 20px;
              display: flex;
              align-items: center;
              cursor: pointer;
              min-width: 170px;
              padding-bottom: 10px;
            `}
          >
            {translate('Manage Organization')}
            {!dropdownOpen && (
              <SvgChevronDown
                css={css`
                  transform: scale(calc(20 / 24)) translateX(4px);
                `}
              />
            )}
            {dropdownOpen && (
              <SvgChevronUp
                css={css`
                  transform: scale(calc(20 / 24)) translateX(4px);
                `}
              />
            )}
          </span>
          <div ref={dropdownOptionsRef}>
            {dropdownOpen && (
              <div
                onClick={(e): void => {
                  e.stopPropagation()
                }}
                onKeyDown={(e): void => {
                  e.stopPropagation()
                }}
                role="button"
                tabIndex={0}
                css={css`
                  z-index: 2;
                  position: absolute;
                  top: 50px;
                  right: 0px;
                  display: inline-block;
                  flex-direction: column;
                  width: 250px;
                  border-radius: ${styleConstants.borderRadius.box};
                  border: 1px solid ${styleConstants.colors.gray_medium};
                  box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.15);
                  overflow: hidden;
                  ${getBreakpointDown(breakpointSizes.XSM)(css`
                    width: auto;
                    right: 20px;
                  `)}
                `}
              >
                {organizationDropdownOptions.length > 0 && (
                  <div
                    css={css`
                      background-color: ${styleConstants.colors.white};
                      border-bottom: 1px solid
                        ${styleConstants.colors.gray_light};
                      padding-bottom: 15px;
                    `}
                  >
                    <span
                      css={css`
                        ${getFontCss(
                          10,
                          styleConstants.colors.gray_dark,
                          styleConstants.fontFamilies.regular,
                        )};
                        display: flex;
                        align-items: center;
                        padding: 20px 20px 5px 20px;
                      `}
                    >
                      {translate('${orgName} (${userRole})', {
                        orgName: props.orgName || '',
                        userRole: translate(user?.orgRoleDisplayTitle || ''),
                      })}
                    </span>
                    {organizationDropdownOptions.map((option, i) => (
                      <Fragment key={i}>
                        <StyledDiv
                          grayed={!!option.grayed}
                          underlined={option.to !== undefined}
                        >
                          {option.to !== undefined ? (
                            <Lnk
                              key={i}
                              to={option.to}
                              {...(option.ariaLabelSource !== undefined
                                ? { ariaLabelSource: option.ariaLabelSource }
                                : { ariaLabel: option.ariaLabel })}
                              css={css`
                                ${option.to !== undefined &&
                                css`
                                  &:hover {
                                    text-decoration: underline;
                                  }
                                `}
                              `}
                              onClick={(): void =>
                                handleOrgInfoAnalytics(option.key)
                              }
                            >
                              {option.label}
                            </Lnk>
                          ) : (
                            <div
                              css={css`
                                padding-bottom: 5px;
                                ${!!option.grayed &&
                                css`
                                  opacity: 0.5;
                                `}
                              `}
                            >
                              {option.label}
                            </div>
                          )}
                          {option.onClick && (
                            <HoverableLink
                              key={i}
                              onClick={option.onClick}
                              {...(option.ariaLabelSource !== undefined
                                ? { ariaLabelSource: option.ariaLabelSource }
                                : { ariaLabel: option.ariaLabel })}
                            >
                              {option.label}
                            </HoverableLink>
                          )}
                        </StyledDiv>
                      </Fragment>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Box>
  )
}

export default OrgAdminBox
